/**
 * Planning mensuel : grille de jours (factorisée dans `components/CalendarGrid.jsx`),
 * navigation mois précédent/suivant, tâches positionnées sur leur intervalle
 * `startDate`→`dueDate`, jalons distincts, jour courant marqué.
 * Un clic sur un jour (ou sur son « +N ») ouvre la vue du jour (`DayTasksModal`) : toutes les
 * tâches du jour, navigation jour à jour, création d'une tâche à cette date.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { useAllTasks } from '../hooks/useAllTasks.js';
import { appAlert, appPrompt } from '../dialogs/service.js';
import { LoadError, TruncatedTasksWarning } from '../components/common.jsx';
import { DayTasksModal } from '../components/DayTasksModal.jsx';
import { CalendarGrid } from '../components/CalendarGrid.jsx';
import { shiftAnchor, todayISO } from '../lib/calendar.js';
import { EYEBROW } from '../ui/theme.js';

export function PlanningView({
  projectId,
  projectKey,
  onOpenTask,
  onChanged,
  canCreate = true,
  reloadKey,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language?.split('-')[0];

  // Jour calendaire LOCAL : `toISO(new Date())` (UTC) affichait la veille entre minuit et 2 h.
  const today = useMemo(() => todayISO(), []);
  const [anchor, setAnchor] = useState(today);
  const [openDay, setOpenDay] = useState(null);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
    [locale],
  );

  // Toutes les pages sont chargées (§ issue B5) : sans ça, des tâches d'un projet volumineux
  // disparaissaient du planning mensuel sans le moindre indicateur.
  const {
    tasks: tasksPage,
    truncated,
    error,
    reload,
  } = useAllTasks(projectId, { deps: [reloadKey] });
  const tasks = tasksPage ?? [];

  const tasksForDay = (iso) =>
    tasks.filter((task) => {
      if (task.kind === 'milestone') return task.dueDate === iso || task.startDate === iso;
      if (!task.startDate || !task.dueDate) return false;
      return task.startDate <= iso && iso <= task.dueDate;
    });

  /**
   * Crée la tâche puis recharge la liste : sans rechargement, la tâche n'apparaissait qu'après
   * un rechargement de page. Quand la page parente fournit `onChanged`, elle bascule
   * `reloadKey` (qui recharge ce hook ET les statistiques de l'en-tête) : un seul aller-retour.
   */
  async function createTaskOnDay(iso) {
    const title = await appPrompt(t('planning.createTaskPrompt', { date: iso }), '');
    if (!title) return;
    try {
      await api.post(`/projects/${projectId}/tasks`, { title, startDate: iso, dueDate: iso });
      if (onChanged) onChanged();
      else reload();
    } catch (error) {
      await appAlert(t('planning.createFailed', { message: error.message }));
    }
  }

  function openTaskFromDay(taskId) {
    setOpenDay(null);
    onOpenTask(taskId);
  }

  const [year, month] = anchor.split('-').map(Number);

  return (
    <div>
      {error && <LoadError error={error} onRetry={reload} className="mb-2" />}
      {truncated && (
        <div className="mb-2">
          <TruncatedTasksWarning />
        </div>
      )}
      <CalendarGrid
        mode="month"
        anchor={anchor}
        today={today}
        locale={locale}
        onShift={(delta) => setAnchor((current) => shiftAnchor(current, 'month', delta))}
        itemsForDay={tasksForDay}
        onDayClick={setOpenDay}
        maxPerDay={3}
        prevLabel={t('planning.prevMonthAria')}
        nextLabel={t('planning.nextMonthAria')}
        title={
          <p className={EYEBROW}>{monthLabel.format(new Date(Date.UTC(year, month - 1, 1)))}</p>
        }
        renderItem={(task) => (
          <span
            key={task.id}
            role="link"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onOpenTask(task.id);
            }}
            className={`truncate px-1 py-0.5 text-[10px] ${
              task.kind === 'milestone'
                ? 'border-2 border-accent-trait text-accent-ink'
                : 'bg-accent-wash text-accent-ink'
            }`}
          >
            {task.kind === 'milestone' ? '◆ ' : ''}
            {task.title}
          </span>
        )}
      />
      {openDay && (
        <DayTasksModal
          day={openDay}
          locale={locale}
          projectKey={projectKey}
          tasksForDay={tasksForDay}
          onShiftDay={setOpenDay}
          onOpenTask={openTaskFromDay}
          onCreate={canCreate ? createTaskOnDay : undefined}
          onClose={() => setOpenDay(null)}
        />
      )}
    </div>
  );
}
